import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ClientAppRoutes from './apps/client/ClientApp';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import PortalShell from './layouts/PortalShell';
import LoginPage from './pages/LoginPage';
import GlobalHome from './pages/GlobalHome';
import ModuleHome from './pages/ModuleHome';
import { useAuth } from './auth/AuthProvider';
// spec-48 W2 — Smart redirect components
import {
  SetupServiceLineRedirect,
  MatterDetailRedirect,
  KanbanRedirect,
  TaskDetailRedirect,
} from './redirects/SetupRedirects';

const S: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense
    fallback={
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress size={24} />
      </Box>
    }
  >
    {children}
  </Suspense>
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <LoginPage />;
  return <>{children}</>;
}

/**
 * FR-153 role gate — BRD §7.1 finance scope.
 * Allows managing_partner + group_finance_officer + entity_accountant.
 * Any other authenticated role is redirected to "/" (portal home).
 */
const FINANCE_ADMIN_ROLES = ['managing_partner', 'group_finance_officer', 'entity_accountant'] as const;

function FinanceAdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user || !FINANCE_ADMIN_ROLES.includes(user.role as typeof FINANCE_ADMIN_ROLES[number])) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * Role-based index redirect (spec-09 QA fix 2026-04-27).
 *
 * Default landing surface per role-class. Spec-09 scenario 2 mandates
 * "Lawyer view does NOT show cross-tenant aggregations" — non-managerial
 * roles must not land on the Manager Dashboard at `/`. Distinct from
 * FR-153 (finance-admin gating, see FINANCE_ADMIN_ROLES above): this
 * controls where each role lands by default; FR-153 controls who can
 * load specific finance admin pages.
 *
 * - managing_partner / group_finance_officer → `/` (Manager Dashboard)
 * - lawyer / team_lead / associate / entity_accountant / admin / pro_support
 *     → `/me` (Lawyer workspace)
 * - client_portal_user → `/client`
 * - unknown → Manager Dashboard (safe fallback)
 */
export const MANAGER_ROLES = ['managing_partner', 'group_finance_officer'] as const;
/**
 * Roles that land on the Lawyer Workspace (/me).
 * MUST be practicing lawyer roles only — not finance/admin/support.
 * entity_accountant, admin, pro_support route to Manager Dashboard (/).
 * spec-09 QA fix-iter-1 (qa-3): removed entity_accountant / admin / pro_support.
 */
export const LAWYER_ROLES = [
  'lawyer',
  'team_lead',
  'associate',
  'senior_lawyer',
  'lead_lawyer',
] as const;

export function isLawyerClassRole(role: string | null | undefined): boolean {
  return !!role && (LAWYER_ROLES as readonly string[]).includes(role);
}

function RoleDefaultRoute() {
  const { user } = useAuth();
  const role = user?.role ?? '';
  if (isLawyerClassRole(role)) return <Navigate to="/me" replace />;
  if (role === 'client_portal_user') return <Navigate to="/client" replace />;
  return <S><ManagerDashboard /></S>;
}

/**
 * Wrapper for the explicit `/dashboard` (Manager Dashboard) route — keeps
 * lawyer-class users from deep-linking past the role gate. Same logic as
 * RoleDefaultRoute, but applied at the route level so `/dashboard` URL bar
 * navigation also redirects to `/me`.
 */
function ManagerDashboardRoute() {
  const { user } = useAuth();
  const role = user?.role ?? '';
  if (isLawyerClassRole(role)) return <Navigate to="/me" replace />;
  if (role === 'client_portal_user') return <Navigate to="/client" replace />;
  return <S><ManagerDashboard /></S>;
}

/** spec-36 W3 — taxonomy admin — managing_partner + team_lead. */
function TaxonomyAdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user || !['managing_partner', 'team_lead'].includes(user.role ?? '')) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/** spec-17 W4 — managing_partner only (Tier 2 standing-delegation authoriser). */
function ManagingPartnerRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user || user.role !== 'managing_partner') {
    return <Navigate to="/delegation/settings" replace />;
  }
  return <>{children}</>;
}

// ===== Event Monitor (built-in) =====
const EventMonitorPage = lazy(() => import('./modules/events/pages/EventMonitorPage'));

// ===== Forge Admin Dashboard =====
const ForgeAdminPage = lazy(() => import('./modules/forge/ForgeAdminPage'));

// ===== Lazy-imported codegen pages from service frontends =====
// JylawDashboard / SetupDashboard / CourtDashboard: codegen never produced
// @jylaw|@setup|@court /dashboard/DashboardPage; re-use existing hand-written
// pages as placeholders (same pattern as RetainerDashboard below).
const JylawDashboard = lazy(() => import('./pages/dashboard/ManagerDashboard'));
const SetupDashboard = lazy(() => import('./pages/setup/SetupDashboard'));
const RetainerDashboard = lazy(() => import('./modules/retainer/pages/RetainerListPage'));
const CourtDashboard = lazy(() => import('./pages/court/Litigation'));

// ===== Setup portal pages (hand-written, W6) =====
const SetupMatters = lazy(() => import('./pages/setup/Matters'));
const SetupMatterDetail = lazy(() => import('./pages/setup/MatterDetail'));
const SetupKanban = lazy(() => import('./pages/setup/Kanban'));
const SetupGridPage = lazy(() => import('./pages/setup/SetupGrid'));
// spec-39 W3.9 — TaskDetail page
const SetupTaskDetail = lazy(() => import('./pages/setup/TaskDetail'));

// ===== spec-48 W2 — Transactions + Post-Incorporation module pages =====
const TransactionsMatters = lazy(() => import('./modules/transactions/pages/Matters'));
const PostIncorporationMatters = lazy(() => import('./modules/post-incorporation/pages/Matters'));

// spec-39 W4.5 — My Tasks page
const MyTasksPage = lazy(() => import('./pages/tasks/MyTasks'));

// ===== Legal Services portal pages (spec-24 W3) =====
const LegalServiceHourLogger = lazy(() => import('./components/legal-services/LegalServiceHourLogger'));

// ===== Retainer portal pages (hand-written, W6 spec-03) =====
const RetainerListPage = lazy(() => import('./modules/retainer/pages/RetainerListPage'));
const RetainerNewPage = lazy(() => import('./modules/retainer/pages/RetainerNewPage'));
const RetainerDetailPage = lazy(() => import('./modules/retainer/pages/RetainerDetailPage'));
const RetainerTimesheetPage = lazy(() => import('./modules/retainer/pages/TimesheetPage'));
const RetainerRenewalsPage = lazy(() => import('./modules/retainer/pages/RenewalsPage'));
const RetainerOveragePage = lazy(() => import('./modules/retainer/pages/OverageRegisterPage'));

// ===== Court portal pages (hand-written, spec-12 stub) =====
const LitigationPage = lazy(() => import('./pages/court/Litigation'));

// ===== Delegation portal pages (hand-written, spec-17 W4) =====
const DelegationSettingsPage = lazy(() => import('./pages/delegation/DelegationSettings'));
const SetDeputyPage = lazy(() => import('./pages/delegation/SetDeputy'));
const AuthoriseStandingPage = lazy(() => import('./pages/delegation/AuthoriseStanding'));

// Wiki
const WikiIndexPage = lazy(() => import('./modules/wiki/pages/WikiIndexPage'));
const WikiPageView = lazy(() => import('./modules/wiki/pages/WikiPageView'));
const WikiIngestPanel = lazy(() => import('./modules/wiki/pages/WikiIngestPanel'));
const WikiLintReport = lazy(() => import('./modules/wiki/pages/WikiLintReport'));

// ===== Privacy / DSAR pages (spec-13 W3.5 + W8 + spec-20 D4) =====
const DsarKanbanPage = lazy(() => import('./pages/privacy/DsarKanban'));
const DsarNewPage = lazy(() => import('./pages/privacy/DsarNew'));
const SdaiaBreachNotificationPage = lazy(() => import('./pages/privacy/SdaiaBreachNotification'));

// ===== OCR Review Queue (spec-07c W5) =====
const OcrReviewPage = lazy(() => import('./pages/ocr-review/OcrReview'));

// ===== Conflict-of-Interest (spec-15 W5 portal patch) =====
const ConflictCheckListPage = lazy(() => import('./pages/conflict/ConflictCheckListPage'));

// ===== Admin pages (spec-14) =====
const AdminBillingEntitiesPage = lazy(() => import('./pages/admin/BillingEntities'));

// ===== Services Taxonomy admin — spec-36 W3 =====
const ServicesTaxonomyView = lazy(() => import('./pages/admin/services-taxonomy/ServicesTaxonomyView'));

// ===== Integrations admin — spec-18 W2.5 / spec-20 G4 / spec-22 W6 =====
const CalendarConnectPage = lazy(() => import('./pages/admin/integrations/CalendarConnect'));
const GmailConnectPage = lazy(() => import('./pages/admin/integrations/GmailConnect'));

// ===== Notification preferences + SLA admin (spec-11.1 W3/W5) =====
const NotificationPreferencesPage = lazy(() => import('./pages/me/NotificationPreferences'));
const SlaRulesPage = lazy(() => import('./pages/admin/SlaRules'));

// ===== Document Templates admin page (spec-07b W3) =====
const DocumentTemplatesPage = lazy(() => import('./pages/admin/document-templates/DocumentTemplatesPage'));

// ===== Dashboard pages (spec-09) =====
const ManagerDashboard = lazy(() => import('./pages/dashboard/ManagerDashboard'));
const LawyerDashboard = lazy(() => import('./pages/dashboard/LawyerDashboard'));
// spec-45 W2 — Expiry Dashboard
const ExpiriesDashboard = lazy(() => import('./pages/dashboard/ExpiriesDashboard'));

// spec-47 W2 — Unified Ops Dashboard
const OpsDashboard = lazy(() => import('./pages/dashboard/OpsDashboard'));

// ===== AI Suggestions feed (spec-42 W6) =====
const SuggestionsFeed = lazy(() => import('./pages/suggestions/SuggestionsFeed'));

// ===== AI Detectors admin (spec-42.5 W3) =====
const AIDetectorsAdmin = lazy(() => import('./pages/admin/AIDetectorsAdmin'));
const AgreementTemplatesAdmin = lazy(() => import('./pages/admin/AgreementTemplatesAdmin'));
// spec-52 W3: LegacyContractCleanup retired — lazy import removed

// ===== Compliance pages (spec-09 Amendment A1 / spec-16 W6.3) =====
const ComplianceStatesPage = lazy(() => import('./pages/compliance/ComplianceStatesPage'));

// ===== Email Inbox pages (spec-04 Wave 5) =====
const EmailInboxPage = lazy(() => import('./pages/email-inbox/Inbox'));
// Rollback / comparison view — old 5-column kanban, kept until the
// Registrar's Docket has soaked in production. Delete the import + route
// once the new design is locked in.
const EmailInboxKanbanPage = lazy(() => import('./pages/email-inbox/InboxKanban'));
const EmailDetailPage = lazy(() => import('./pages/email-inbox/EmailDetail'));

// ===== CRM portal pages (hand-written, spec-05 W6) =====
const CrmPipelinePage = lazy(() => import('./pages/crm/Pipeline'));
const CrmDealDetailPage = lazy(() => import('./pages/crm/DealDetail'));
const CrmCompanyPage = lazy(() => import('./pages/crm/Company'));
const CrmCompaniesListPage = lazy(() => import('./pages/crm/companies/CompaniesListPage'));
// Setup contracts — spec-37 W3
const CrmSetupContractDetailPage = lazy(() => import('./pages/crm/SetupContractDetail'));
const CrmDashboardPage = lazy(() => import('./pages/crm/Dashboard'));
// spec-41 W4.6 — Proposal detail page
const CrmProposalDetailPage = lazy(() => import('./pages/crm/ProposalDetail'));

// ===== Clients pages — Editorial Counsel redesign =====
const NewClientPage = lazy(() => import('./pages/clients/NewClient'));

// ===== Billing pages (spec-06 W9) =====
const BillingInvoicesPage = lazy(() => import('./pages/billing/Invoices'));
const BillingInvoiceDetailPage = lazy(() => import('./pages/billing/InvoiceDetail'));
const BillingAgingPage = lazy(() => import('./pages/billing/Aging'));

// ===== Documents pages (spec-07 W5) =====
const DocumentsBrowse = lazy(() => import('./pages/documents/Browse'));
const DocumentDetailPage = lazy(() => import('./pages/documents/DocumentDetail'));

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* Client portal — spec-10. Mounts at /client/* */}
      <Route path="/client/*" element={<ClientAppRoutes />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <PortalShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<RoleDefaultRoute />} />
        <Route path="home" element={<GlobalHome />} />
        <Route path="dashboard" element={<ManagerDashboardRoute />} />
        {/* spec-45 W2 — Expiry Dashboard: managing_partner, team_lead, lawyer */}
        <Route path="dashboard/expiries" element={<S><ExpiriesDashboard /></S>} />
        {/* spec-47 W2 — Unified Ops Dashboard */}
        <Route path="dashboard/ops" element={<S><OpsDashboard /></S>} />
        <Route path="me" element={<S><LawyerDashboard /></S>} />
        {/* spec-39 W4.5 — My Tasks cross-module page */}
        <Route path="tasks" element={<S><MyTasksPage /></S>} />
        {/* spec-42 W6 — AI suggestions feed */}
        <Route path="suggestions" element={<S><SuggestionsFeed /></S>} />

{/* Events module */}
        <Route path="events" element={<S><EventMonitorPage /></S>} />

{/* Forge admin */}
        <Route path="forge/admin" element={<S><ForgeAdminPage /></S>} />

{/* Module home pages */}
          <Route path="jylaw" element={<S><ModuleHome moduleId="jylaw" /></S>} />
          <Route path="setup-registration" element={<S><ModuleHome moduleId="setup-registration" /></S>} />
          <Route path="retainer" element={<S><ModuleHome moduleId="retainer" /></S>} />
          <Route path="court" element={<S><ModuleHome moduleId="court" /></S>} />
          <Route path="delegation" element={<S><ModuleHome moduleId="delegation" /></S>} />
          <Route path="crm" element={<S><CrmDashboardPage /></S>} />
          <Route path="legal-services" element={<S><ModuleHome moduleId="legal-services" /></S>} />

{/* Admin CRUD pages (from service frontends) */}
          <Route path="jylaw/dashboard" element={<S><JylawDashboard /></S>} />
          <Route path="retainer/dashboard" element={<S><RetainerDashboard /></S>} />
          <Route path="court/dashboard" element={<S><CourtDashboard /></S>} />
          {/* spec-48 W2: /setup/dashboard → /setup-registration/dashboard (302) */}
          <Route path="setup/dashboard" element={<Navigate to="/setup-registration/dashboard" replace />} />
          <Route path="setup-registration/dashboard" element={<S><SetupDashboard /></S>} />

{/* spec-48 W2 — Setup-Registration portal pages (renamed from setup) */}
          <Route path="setup-registration/matters" element={<S><SetupMatters serviceLine="setup_registration" /></S>} />
          <Route path="setup-registration/matters/:id" element={<S><SetupMatterDetail /></S>} />
          {/* spec-39 W3.9 — TaskDetail */}
          <Route path="setup-registration/matters/:matter_id/tasks/:task_id" element={<S><SetupTaskDetail /></S>} />
          <Route path="setup-registration/kanban/:txTypeCode" element={<S><SetupKanban serviceLine="setup_registration" /></S>} />
          <Route path="setup-registration/grid" element={<S><SetupGridPage /></S>} />

{/* spec-48 W2 — Transactions module portal pages */}
          <Route path="transactions" element={<S><ModuleHome moduleId="transactions" /></S>} />
          <Route path="transactions/matters" element={<S><TransactionsMatters /></S>} />
          <Route path="transactions/matters/:id" element={<S><SetupMatterDetail /></S>} />
          <Route path="transactions/matters/:matter_id/tasks/:task_id" element={<S><SetupTaskDetail /></S>} />
          <Route path="transactions/kanban/:txTypeCode" element={<S><SetupKanban serviceLine="transactions" /></S>} />
          <Route path="transactions/grid" element={<S><SetupGridPage /></S>} />

{/* spec-48 W2 — Post-Incorporation module portal pages */}
          <Route path="post-incorporation" element={<S><ModuleHome moduleId="post-incorporation" /></S>} />
          <Route path="post-incorporation/matters" element={<S><PostIncorporationMatters /></S>} />
          <Route path="post-incorporation/matters/:id" element={<S><SetupMatterDetail /></S>} />
          <Route path="post-incorporation/matters/:matter_id/tasks/:task_id" element={<S><SetupTaskDetail /></S>} />

{/* spec-48 W2 — Old /setup/* redirects (302 via React Router Navigate) */}
          {/* Simple static redirects */}
          <Route path="setup" element={<Navigate to="/setup-registration" replace />} />
          <Route path="setup/grid" element={<Navigate to="/setup-registration/grid" replace />} />
          {/* Smart redirect: /setup/matters?service_line=X → /<line>/matters */}
          <Route path="setup/matters" element={<SetupServiceLineRedirect />} />
          {/* Smart redirect: /setup/matters/:id → /<line>/matters/:id (fetches service_line) */}
          <Route path="setup/matters/:id" element={<MatterDetailRedirect />} />
          {/* Smart redirect: /setup/matters/:matter_id/tasks/:task_id */}
          <Route path="setup/matters/:matter_id/tasks/:task_id" element={<TaskDetailRedirect />} />
          {/* Smart redirect: /setup/kanban/:txTypeCode → /<line>/kanban/:txTypeCode */}
          <Route path="setup/kanban/:txTypeCode" element={<KanbanRedirect />} />

{/* Legal Services portal pages — spec-24 W3 */}
          <Route path="legal-services/matters" element={<S><SetupMatters serviceLine="legal_services" /></S>} />
          <Route path="legal-services/matters/:id" element={<S><SetupMatterDetail /></S>} />
          <Route path="legal-services/kanban/:txTypeCode" element={<S><SetupKanban serviceLine="legal_services" /></S>} />
          <Route path="legal-services/log-hours" element={<S><LegalServiceHourLogger /></S>} />

{/* Retainer portal pages — W6 spec-03 hand-written */}
          <Route path="retainer/list" element={<S><RetainerListPage /></S>} />
          <Route path="retainer/new" element={<S><RetainerNewPage /></S>} />
          <Route path="retainer/:id" element={<S><RetainerDetailPage /></S>} />
          <Route path="retainer/timesheet" element={<S><RetainerTimesheetPage /></S>} />
          <Route path="retainer/renewals" element={<S><RetainerRenewalsPage /></S>} />
          <Route path="retainer/overages" element={<S><RetainerOveragePage /></S>} />

{/* Court portal pages — spec-12 stub hand-written */}
          <Route path="court/litigation" element={<S><LitigationPage /></S>} />

{/* Delegation portal pages — spec-17 W4 hand-written */}
          <Route path="delegation/settings" element={<S><DelegationSettingsPage /></S>} />
          <Route path="delegation/set-deputy" element={<S><SetDeputyPage /></S>} />
          <Route
            path="delegation/authorise-standing"
            element={
              <ManagingPartnerRoute>
                <S><AuthoriseStandingPage /></S>
              </ManagingPartnerRoute>
            }
          />

      {/* Wiki */}
      <Route path="wiki" element={<S><WikiIndexPage /></S>} />
      <Route path="wiki/page/:slug" element={<S><WikiPageView /></S>} />
      <Route path="wiki/ingest" element={<S><WikiIngestPanel /></S>} />
      <Route path="wiki/lint" element={<S><WikiLintReport /></S>} />

      {/* Admin pages — spec-14. FR-153: managing_partner + group_finance_officer only. */}
          <Route
            path="admin/billing-entities"
            element={
              <FinanceAdminRoute>
                <S><AdminBillingEntitiesPage /></S>
              </FinanceAdminRoute>
            }
          />

      {/* Services Taxonomy admin — spec-36 W3. managing_partner + team_lead. */}
          <Route
            path="admin/services-taxonomy"
            element={
              <TaxonomyAdminRoute>
                <S><ServicesTaxonomyView /></S>
              </TaxonomyAdminRoute>
            }
          />

      {/* Document Templates admin — spec-07b W3. RBAC: manager only. */}
          <Route
            path="admin/document-templates"
            element={
              <FinanceAdminRoute>
                <S><DocumentTemplatesPage /></S>
              </FinanceAdminRoute>
            }
          />

      {/* Email Inbox pages — spec-04 Wave 5 */}
          <Route path="email-inbox" element={<S><EmailInboxPage /></S>} />
          {/* Rollback path — keep static 'kanban' BEFORE the parametric
              :emailId route so it doesn't get treated as an email id. */}
          <Route path="email-inbox/kanban" element={<S><EmailInboxKanbanPage /></S>} />
          <Route path="email-inbox/:emailId" element={<S><EmailDetailPage /></S>} />

      {/* CRM portal pages — spec-05 W6 hand-written */}
          <Route path="crm/dashboard" element={<S><CrmDashboardPage /></S>} />
          <Route path="crm/pipeline" element={<S><CrmPipelinePage /></S>} />
          <Route path="crm/deals/:id" element={<S><CrmDealDetailPage /></S>} />
          <Route path="crm/companies" element={<S><CrmCompaniesListPage /></S>} />
          <Route path="crm/companies/:id" element={<S><CrmCompanyPage /></S>} />
          {/* Setup contracts — spec-37 W3 */}
          <Route path="crm/setup-contracts/:id" element={<S><CrmSetupContractDetailPage /></S>} />
          {/* Proposal detail — spec-41 W4.6 */}
          <Route path="crm/proposals/:id" element={<S><CrmProposalDetailPage /></S>} />

      {/* Clients — standalone create page (Editorial Counsel design) */}
          <Route path="clients/new" element={<S><NewClientPage /></S>} />

      {/* Billing pages — spec-06 W9 hand-written */}
          <Route path="billing/invoices" element={<S><BillingInvoicesPage /></S>} />
          <Route path="billing/invoices/:id" element={<S><BillingInvoiceDetailPage /></S>} />
          <Route
            path="billing/aging"
            element={
              <FinanceAdminRoute>
                <S><BillingAgingPage /></S>
              </FinanceAdminRoute>
            }
          />

      {/* Documents pages — spec-07 W5 */}
          <Route path="documents" element={<S><ModuleHome moduleId="documents" /></S>} />
          <Route path="documents/browse" element={<S><DocumentsBrowse /></S>} />
          <Route path="documents/:id" element={<S><DocumentDetailPage /></S>} />

      {/* Notification preferences — spec-11.1 W3 */}
          <Route path="me/notifications" element={<S><NotificationPreferencesPage /></S>} />

      {/* Compliance — spec-09 A1 / spec-16 W6.3 */}
          <Route path="compliance" element={<Navigate to="/compliance/states" replace />} />
          <Route path="compliance/states" element={<S><ComplianceStatesPage /></S>} />

      {/* SLA Rules admin — spec-11.1 W5. Managing partner only. */}
          <Route
            path="admin/sla-rules"
            element={
              <ManagingPartnerRoute>
                <S><SlaRulesPage /></S>
              </ManagingPartnerRoute>
            }
          />

      {/* AI Detectors admin — spec-42.5 W3. Managing partner only. */}
          <Route
            path="admin/ai-detectors"
            element={
              <ManagingPartnerRoute>
                <S><AIDetectorsAdmin /></S>
              </ManagingPartnerRoute>
            }
          />

      {/* Agreement Templates admin — spec-49 W2. Managing partner only. */}
          <Route
            path="admin/agreement-templates"
            element={
              <ManagingPartnerRoute>
                <S><AgreementTemplatesAdmin /></S>
              </ManagingPartnerRoute>
            }
          />

      {/* Legacy Contract Cleanup — spec-52 W3: retired, redirect to agreement templates */}
          <Route
            path="admin/legacy-deliverable-cleanup"
            element={<Navigate to="/admin/agreement-templates?retired=1" replace />}
          />

      {/* Privacy / DSAR pages — spec-13 W3.5 + W8 + spec-20 D4. Privacy officer + managing partner. */}
          <Route path="admin/privacy/dsar" element={<S><DsarKanbanPage /></S>} />
          <Route path="admin/privacy/dsar/new" element={<S><DsarNewPage /></S>} />
          <Route path="admin/privacy/dsar/:dsarId/sdaia-notification" element={<S><SdaiaBreachNotificationPage /></S>} />

      {/* OCR Review Queue — spec-07c W5. Documents staff + managing partner. */}
          <Route path="ocr-review" element={<S><OcrReviewPage /></S>} />

      {/* Calendar Connect — spec-18 W2.5 / spec-20 G4. All authenticated lawyers. */}
          <Route path="admin/integrations/calendar" element={<S><CalendarConnectPage /></S>} />

      {/* Gmail Connect — spec-22 W6. All internal roles; client_portal_user excluded (role gate on backend). */}
          <Route path="admin/integrations/gmail" element={<S><GmailConnectPage /></S>} />

      {/* Conflict-of-interest check queue — spec-15 W5. Managing partner only. */}
          <Route
            path="conflict"
            element={
              <ManagingPartnerRoute>
                <S><ConflictCheckListPage /></S>
              </ManagingPartnerRoute>
            }
          />
</Route>
    </Routes>
  );
}
